import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { DollarSign, Save, Loader2, Mail, Send, Eye, EyeOff, CheckCircle2, XCircle, Globe } from "lucide-react";
import { toast } from "../components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OwnerGeralPage() {
  const [pricing, setPricing] = useState({ base_price: 49, extra_user_price: 10, included_users: 1 });
  const [smtp, setSmtp] = useState({ smtp_host: "", smtp_port: 587, smtp_user: "", smtp_pass: "", sender_email: "" });
  const [platformUrl, setPlatformUrl] = useState("");
  const [savingPricing, setSavingPricing] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [savingUrl, setSavingUrl] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/owner/pricing`, { withCredentials: true }).then(r => setPricing(r.data)).catch(() => {}),
      axios.get(`${API}/owner/smtp`, { withCredentials: true }).then(r => setSmtp(r.data)).catch(() => {}),
      axios.get(`${API}/owner/platform-url`, { withCredentials: true }).then(r => setPlatformUrl(r.data.url || "")).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      await axios.put(`${API}/owner/pricing`, pricing, { withCredentials: true });
      toast.success("Precificação salva com sucesso!");
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSavingPricing(false);
    }
  };

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      await axios.put(`${API}/owner/smtp`, smtp, { withCredentials: true });
      toast.success("Configuração SMTP salva com sucesso!");
    } catch {
      toast.error("Erro ao salvar SMTP");
    } finally {
      setSavingSmtp(false);
    }
  };

  const sendTest = async () => {
    if (!smtp.smtp_host || !smtp.smtp_user || !smtp.smtp_pass) {
      toast.error("Preencha todos os campos SMTP antes de testar.");
      return;
    }
    setTestingSmtp(true);
    try {
      const res = await axios.post(`${API}/owner/smtp/test`, { ...smtp, test_email: testEmail }, { withCredentials: true });
      toast.success(res.data.message || "E-mail de teste enviado!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no envio de teste.");
    } finally {
      setTestingSmtp(false);
    }
  };

  const savePlatformUrl = async () => {
    if (!platformUrl.trim()) {
      toast.error("URL é obrigatória.");
      return;
    }
    setSavingUrl(true);
    try {
      await axios.put(`${API}/owner/platform-url`, { url: platformUrl.trim() }, { withCredentials: true });
      toast.success("URL da plataforma salva com sucesso!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar URL");
    } finally {
      setSavingUrl(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900" data-testid="owner-geral-title">Configurações Gerais</h1>
        <p className="text-sm text-slate-500 mt-1">Defina os valores padrão da plataforma e configure o envio de e-mails.</p>
      </div>

      {/* Platform URL Card */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Globe className="w-4 h-4 text-emerald-500" />
            URL da Plataforma
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Define o domínio usado nos links dos e-mails transacionais (convites, redefinição de senha, welcome) e nas imagens do e-mail.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs text-slate-500">URL principal</Label>
            <Input
              data-testid="platform-url-input"
              placeholder="https://pipe4.me"
              value={platformUrl}
              onChange={e => setPlatformUrl(e.target.value)}
            />
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
            <p>Todos os links nos e-mails transacionais apontarão para este domínio. Certifique-se de que o domínio esteja ativo e apontando para o servidor correto.</p>
          </div>
          <div className="flex justify-end">
            <Button data-testid="save-platform-url-btn" onClick={savePlatformUrl} disabled={savingUrl} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {savingUrl ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar URL
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Card */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <DollarSign className="w-4 h-4 text-blue-500" />
            Precificação padrão
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Valor base (BRL/mês)</Label>
              <Input data-testid="pricing-base-price" type="number" step="0.01" min="0" value={pricing.base_price}
                onChange={e => setPricing(p => ({ ...p, base_price: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Usuários inclusos</Label>
              <Input data-testid="pricing-included-users" type="number" min="1" value={pricing.included_users}
                onChange={e => setPricing(p => ({ ...p, included_users: parseInt(e.target.value) || 1 }))} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Valor por usuário adicional (BRL/mês)</Label>
              <Input data-testid="pricing-extra-user" type="number" step="0.01" min="0" value={pricing.extra_user_price}
                onChange={e => setPricing(p => ({ ...p, extra_user_price: parseFloat(e.target.value) || 0 }))} />
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
            <p>Exemplo: 5 usuários = R${pricing.base_price.toFixed(2)} + {Math.max(0, 5 - pricing.included_users)}×R${pricing.extra_user_price.toFixed(2)} = <strong className="text-slate-800">R${(pricing.base_price + Math.max(0, 5 - pricing.included_users) * pricing.extra_user_price).toFixed(2)}/mês</strong></p>
          </div>
          <div className="flex justify-end">
            <Button data-testid="save-pricing-btn" onClick={savePricing} disabled={savingPricing} className="bg-blue-600 hover:bg-blue-700 text-white">
              {savingPricing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar precificação
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* SMTP Card */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="w-4 h-4 text-indigo-500" />
            Servidor de E-mail (SMTP)
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">Configure o servidor SMTP para envio de e-mails transacionais (convites, redefinição de senha, etc.).</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Host SMTP</Label>
              <Input data-testid="smtp-host" placeholder="smtp.example.com" value={smtp.smtp_host}
                onChange={e => setSmtp(p => ({ ...p, smtp_host: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Porta</Label>
              <Input data-testid="smtp-port" type="number" placeholder="587" value={smtp.smtp_port}
                onChange={e => setSmtp(p => ({ ...p, smtp_port: parseInt(e.target.value) || 587 }))} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Usuário SMTP</Label>
              <Input data-testid="smtp-user" placeholder="noreply@suaempresa.com" value={smtp.smtp_user}
                onChange={e => setSmtp(p => ({ ...p, smtp_user: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Senha SMTP</Label>
              <div className="relative">
                <Input data-testid="smtp-pass" type={showPass ? "text" : "password"} placeholder="••••••••" value={smtp.smtp_pass}
                  onChange={e => setSmtp(p => ({ ...p, smtp_pass: e.target.value }))} />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">E-mail remetente</Label>
            <Input data-testid="smtp-sender" placeholder="noreply@suaempresa.com" value={smtp.sender_email}
              onChange={e => setSmtp(p => ({ ...p, sender_email: e.target.value }))} />
            <p className="text-[11px] text-slate-400 mt-1">Se vazio, será usado o mesmo do usuário SMTP.</p>
          </div>

          {/* Status indicator */}
          {smtp.smtp_host ? (
            <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>SMTP configurado ({smtp.smtp_host}:{smtp.smtp_port})</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
              <XCircle className="w-4 h-4" />
              <span>SMTP não configurado — e-mails transacionais não serão enviados</span>
            </div>
          )}

          {/* Test section */}
          <div className="border-t border-slate-100 pt-4">
            <Label className="text-xs text-slate-500 mb-2 block">Enviar e-mail de teste</Label>
            <div className="flex gap-2">
              <Input data-testid="smtp-test-email" placeholder="seu@email.com" value={testEmail}
                onChange={e => setTestEmail(e.target.value)} className="flex-1" />
              <Button data-testid="smtp-test-btn" variant="outline" onClick={sendTest} disabled={testingSmtp || !smtp.smtp_host}>
                {testingSmtp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Testar
              </Button>
            </div>
          </div>

          <div className="flex justify-end">
            <Button data-testid="save-smtp-btn" onClick={saveSmtp} disabled={savingSmtp} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {savingSmtp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar SMTP
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
